import { Box, Typography } from '@mui/material';
import { OfficialAvatar } from '@components';
import { OfficialData, Verification } from '../../../networkRequest/officialsService';
import { GridColDef, GridEditSingleSelectCell } from '@mui/x-data-grid';
import { EditPencilIcon } from '../../../assets/icons/icons';
import { PendingDisplay, VerifiedButton, DenyButton, UserInfoButton, VerifiedIcon, UnVerifiedIcon } from './TableComponents'; 
import Utils from '../../../utils/utils';

// working with an array of values for one column is not ideal in MUI (would require custom sorting, transformations for displaying and getting data, etc.)
export interface OfficialWithSingleVerification
  extends Omit<OfficialData, 'post_verifications' | 'agency_verifications'> {
  post_verifications: Verification | null;
  agency_verifications: Verification | null;
  notes?: string | null;
}

//v1, v2 are parameter names used in MUI for sort comparators
const sortVerifications = (v1: string, v2: string) => {
  const statusOrder = {
    pending: 0,
    verified: 1,
    unverified: 2
  };

  if (statusOrder[v1] < statusOrder[v2]) return -1;
  if (statusOrder[v1] > statusOrder[v2]) return 1;
  return 0;
};

export const CreateInternalVerificationsColumns = (
  onNotesClick: (official: OfficialWithSingleVerification, anchorEl: HTMLElement) => void
): GridColDef[] => {
  return [
    {
      field: 'first_name',
      headerName: 'First Name',
      editable: false,
      renderCell: (params: { row: OfficialData }) => (
        <OfficialAvatar
          img_url={params.row.img_url}
          name={params.row.first_name}
          square={false}
          officialTableView={true}
        />
      ),
      width: 150
    },
    {
      field: 'last_name',
      headerName: 'Last Name',
      editable: false,
      width: 150
    },
    {
      field: 'user_info',
      headerName: 'User Info',
      editable: false,
      sortable: false,
      renderCell: (params: { row: OfficialWithSingleVerification }) => (
        <UserInfoButton officialId={params.row.id} />
      ),
      width: 150
    },
    {
      field: 'post_verifications',
      headerName: 'POST Verification',
      editable: true,
      display: 'flex',
      type: 'singleSelect' as const,
      valueOptions: [
        { value: 'pending', label: 'Pending' },
        {
          value: 'verified',
          label: <VerifiedButton />
        },
        {
          value: 'unverified',
          label: <DenyButton />
        }
      ],
      renderCell: (params: { row: OfficialWithSingleVerification }) => {
        if (!params.row.post_verifications) return <PendingDisplay />;
        if (params.row.post_verifications.status === 'pending')
          return <PendingDisplay />;
        if (params.row.post_verifications.status === 'verified') return 'Verified';
        if (params.row.post_verifications.status === 'unverified') return 'Denied';
      },
      renderEditCell: (params) => {
        // Hide 'Pending' from edit dropdown (available for filtering only)
        const editOptions = (params.colDef as any).valueOptions.filter(
          (opt) => opt.value !== 'pending'
        );

        return (
          <GridEditSingleSelectCell
            {...params}
            value={params.value === 'pending' ? '' : params.value}
            colDef={{ ...params.colDef, valueOptions: editOptions } as any}
          />
        );
      },
      valueGetter: (_, row) => {
        if (!row.post_verifications) return 'pending';
        return row.post_verifications.status;
      },
      sortComparator: sortVerifications,
      getApplyQuickFilterFn: (filterValue) => {
        return (cellValue) => {
          if (cellValue === 'unverified') cellValue = 'denied';
          return cellValue.toLowerCase().includes(filterValue.toLowerCase());
        };
      },
      width: 165
    },
    {
      field: 'agency_verifications',
      headerName: 'Agency Verification',
      editable: true,
      display: 'flex',
      type: 'singleSelect' as const,
      valueOptions: [
        { value: 'pending', label: 'Pending' },
        {
          value: 'verified',
          label: <VerifiedButton />
        },
        {
          value: 'unverified',
          label: <DenyButton />
        }
      ],
      renderCell: (params: { row: OfficialWithSingleVerification }) => {
        if (!params.row.agency_verifications) return <PendingDisplay />;
        if (params.row.agency_verifications.status === 'pending')
          return <PendingDisplay />;
        if (params.row.agency_verifications.status === 'verified') return 'Verified';
        if (params.row.agency_verifications.status === 'unverified') return 'Denied';
      },
      renderEditCell: (params) => {
        // Hide 'Pending' from edit dropdown (available for filtering only)
        const editOptions = (params.colDef as any).valueOptions.filter(
          (opt) => opt.value !== 'pending'
        );

        return (
          <GridEditSingleSelectCell
            {...params}
            value={params.value === 'pending' ? '' : params.value}
            colDef={{ ...params.colDef, valueOptions: editOptions } as any}
          />
        );
      },
      valueGetter: (_, row) => {
        if (!row.agency_verifications) return 'pending';
        return row.agency_verifications.status;
      },
      sortComparator: sortVerifications,
      getApplyQuickFilterFn: (filterValue) => {
        return (cellValue) => {
          if (cellValue === 'unverified') cellValue = 'denied';
          return cellValue.toLowerCase().includes(filterValue.toLowerCase());
        };
      },
      width: 165
    },
    {
      field: 'email_verified',
      headerName: 'Email Verified',
      editable: false,
      renderCell: (params: { row: OfficialWithSingleVerification }) => {
        return params.row.email_verified ? 'Verified' : 'Unverified';
      },
      valueGetter: (_, row) => {
        return row.email_verified ? 'verified' : 'unverified';
      },
      getApplyQuickFilterFn: (filterValue) => {
        return (cellValue) => {
          return cellValue.toLowerCase().includes(filterValue.toLowerCase());
        };
      },
      width: 140
    },
    {
      field: 'verified',
      headerName: 'SurePass Verified',
      editable: false,
      renderCell: (params: { row: OfficialWithSingleVerification }) => {
        return params.row.verified ? <VerifiedIcon /> : <UnVerifiedIcon />;
      },
      width: 140
    },
    {
      field: 'title',
      headerName: 'Title',
      editable: false,
      width: 150
    },
    {
      field: 'badge_number',
      headerName: 'Badge Number',
      editable: false,
      width: 100
    },
    {
      field: 'agency_name',
      headerName: 'Agency',
      valueGetter: (_, row) => {
        return row.agency.name;
      },
      editable: false,
      width: 150
    },
    {
      field: 'rank',
      headerName: 'Rank',
      editable: false,
      width: 150
    },
    {
      field: 'agency_contact_email',
      headerName: 'Contact Email',
      valueGetter: (_, row) => {
        return row.agency.contact_email;
      },
      editable: false,
      width: 200
    },
    {
      field: 'email',
      headerName: "Official's Email",
      editable: false,
      width: 200
    },
    {
      field: 'notes',
      headerName: 'Notes',
      editable: false, // Using EditNotesModal for editing here, not MUI DataGrid
      width: 120,
      renderCell: (params: { row: OfficialWithSingleVerification }) => (
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
          onClick={(event) => onNotesClick(params.row, event.currentTarget)}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '35px',
              height: '35px',
              padding: '0 5.833px 0.012px 4.375px',
              gap: '4.451px'
            }}>
            <EditPencilIcon />
            <Typography
              variant="caption"
              sx={{ fontSize: '10px', fontWeight: 600, color: '#2B455E' }}>
              NOTE
            </Typography>
          </Box>
        </Box>
      )
    }
  ];
};

export const agencyVerificationcColumns: GridColDef[] = [
  {
    field: 'name',
    headerName: 'Name',
    editable: false,
    renderCell: (params: { row: OfficialData }) => (
      <OfficialAvatar
        img_url={params.row.img_url}
        name={Utils.fullName(params.row.first_name, params.row.last_name)}
        square={false}
        officialTableView={true}
      />
    ),
    valueGetter: (_, row) => {
      // Construct the full name for search/filter/sort
      return Utils.fullName(row.first_name, row.last_name);
    },
    width: 175,
  },
  {
    field: 'agency_verifications',
    headerName: 'Agency Verified',
    editable: true,
    display: 'flex',
    type: 'singleSelect' as const,
    valueOptions: [
      { value: 'pending', label: 'Pending' },
      {
        value: 'verified',
        label: <VerifiedButton />
      },
      {
        value: 'unverified',
        label: <DenyButton />
      }
    ],
    renderCell: (params: { row: OfficialWithSingleVerification }) => {
      if (!params.row.agency_verifications) return <PendingDisplay />;
      if (params.row.agency_verifications.status === 'pending')
        return <PendingDisplay />;
      if (params.row.agency_verifications.status === 'verified') return 'Verified';
      if (params.row.agency_verifications.status === 'unverified') return 'Denied';
    },
    renderEditCell: (params) => {
      // Hide 'Pending' from edit dropdown (available for filtering only)
      const editOptions = (params.colDef as any).valueOptions.filter(
        (opt) => opt.value !== 'pending'
      );

      return (
        <GridEditSingleSelectCell
          {...params}
          value={params.value === 'pending' ? '' : params.value}
          colDef={{ ...params.colDef, valueOptions: editOptions } as any}
        />
      );
    },
    valueGetter: (_, row) => {
      if (!row.agency_verifications) return 'pending';
      return row.agency_verifications.status;
    },
    sortComparator: sortVerifications,
    getApplyQuickFilterFn: (filterValue) => {
      return (cellValue) => {
        if (cellValue === 'unverified') cellValue = 'denied';
        return cellValue.toLowerCase().includes(filterValue.toLowerCase());
      };
    },
    width: 150
  },
  {
    field: 'verified',
    headerName: 'SurePass Verified',
    editable: false,
    renderCell: (params: { row: OfficialWithSingleVerification }) => {
      return params.row.verified ? <VerifiedIcon /> : <UnVerifiedIcon />;
    },
    width: 140
  },
  {
    field: 'user_info',
    headerName: 'User Info',
    editable: false,
    sortable: false,
    renderCell: (params: { row: OfficialWithSingleVerification }) => (
      <UserInfoButton officialId={params.row.id} />
    ),
    width: 150
  },
  {
    field: 'badge_number',
    headerName: 'Badge Number',
    editable: false,
    width: 100
  },
  {
    field: 'title',
    headerName: 'Title',
    editable: false,
    width: 150
  },
  {
    field: 'rank',
    headerName: 'Rank',
    editable: false,
    width: 150
  },
  {
    field: 'email',
    headerName: "Official's Email",
    editable: false,
    width: 200
  },
];
